import {
  createArchive, getArchives, updateArchive, deleteArchive,
} from 'client/store/entities/hierarchy/archive/archive';
import {
  createCountry, getCountries, getCountry, updateCountry, deleteCountry,
} from 'client/store/entities/hierarchy/country/country';
import {
  createField, getField, getFields, updateField, deleteField,
} from 'client/store/entities/hierarchy/field/field';
import {
  createSite, deleteSite, getSite, getSites, updateSite,
} from 'client/store/entities/hierarchy/site/site';
import {
  createWell, deleteWell, getWell, getWells, updateWell,
} from 'client/store/entities/hierarchy/well/well';
import {
  createWellbore, deleteWellbore, getWellbore, getWellbores, updateWellbore,
} from 'client/store/entities/hierarchy/wellbore/wellbore';
import {
  deleteNode, getNode, getNodes, updateNode,
} from 'client/store/entities/hierarchy/node/node';
import { HierarchyLevelType, IHierarchyAPIQuery, SecurityClassification } from 'common/interfaces/hierarchy-entities';
import { deleteObject, updateName } from 'client/store/entities/object/object';
import {
  createDesign, deleteDesign, getDesign, getDesigns, updateDesign,
} from 'client/store/entities/hierarchy/design/design';
import { IHierarchyTreeItem, TSelectedItem } from './hierarchy.interfaces';
import { hierarchyTypeNewLabel } from './hierarchy.utils';

type TEntityCrudOperations = {
  create?: Function;
  update?: Function;
  getOne?: Function;
  getMany?: Function;
  delete: Function;
};

export const ENTITY_ACTIONS_MAPPER: { [key in HierarchyLevelType]: TEntityCrudOperations | null } = {
  [HierarchyLevelType.Archive]: {
    getMany: getArchives,
    create: createArchive,
    update: updateArchive,
    delete: deleteArchive,
  },
  [HierarchyLevelType.Country]: {
    getMany: getCountries,
    getOne: getCountry,
    create: createCountry,
    update: updateCountry,
    delete: deleteCountry,
  },
  [HierarchyLevelType.Field]: {
    getMany: getFields,
    getOne: getField,
    create: createField,
    update: updateField,
    delete: deleteField,
  },
  [HierarchyLevelType.Site]: {
    getMany: getSites,
    getOne: getSite,
    create: createSite,
    update: updateSite,
    delete: deleteSite,
  },
  [HierarchyLevelType.Well]: {
    getMany: getWells,
    getOne: getWell,
    create: createWell,
    update: updateWell,
    delete: deleteWell,
  },
  [HierarchyLevelType.Wellbore]: {
    getMany: getWellbores,
    getOne: getWellbore,
    create: createWellbore,
    update: updateWellbore,
    delete: deleteWellbore,
  },
  [HierarchyLevelType.Design]: {
    getMany: getDesigns,
    getOne: getDesign,
    create: createDesign,
    update: updateDesign,
    delete: deleteDesign,
  },
  [HierarchyLevelType.Node]: {
    getMany: getNodes,
    getOne: getNode,
    update: updateNode,
    delete: deleteNode,
  },
  [HierarchyLevelType.Object]: {
    update: updateName,
    delete: deleteObject,
  },
};

export const mapTreeItemToEntity = (entity: Partial<TSelectedItem & { parent?: any }>) => {
  const metadata = entity.metadata || {};
  switch (entity.entityType) {
    case HierarchyLevelType.Archive:
      return {
        ...(entity.id && { archiveid: entity.id }),
        name: entity.name,
      };
    case HierarchyLevelType.Country:
      return {
        ...entity,
        ...(entity.nodeId && { nodeid: entity.nodeId }),
        archiveid: entity.parentId,
        metadata,
      };
    case HierarchyLevelType.Field:
      return {
        ...entity,
        ...(entity.nodeId && { nodeid: entity.nodeId }),
        metadata,
      };
    case HierarchyLevelType.Site:
      return {
        ...entity,
        ...(entity.nodeId && { nodeid: entity.nodeId }),
        metadata,
      };
    case HierarchyLevelType.Well:
      return {
        ...entity,
        ...(entity.nodeId && { nodeid: entity.nodeId }),
        metadata,
      };
    case HierarchyLevelType.Wellbore:
      return {
        ...entity,
        ...(entity.nodeId && { nodeid: entity.nodeId }),
        metadata,
      };
    case HierarchyLevelType.Design: {
      return {
        ...(entity.id && { designid: entity.id }),
        name: entity.name,
        ...(entity.nodeId && { nodeid: entity.nodeId }),
        wellboreid: entity.parentId,
        well_status: entity.well_status,
        //@ts-ignore
        state: entity.state,
        metadata,
      };
    }
    case HierarchyLevelType.Node: {
      const { parentid = null } = entity as any;
      return {
        ...(entity.id && { nodeid: entity.id }),
        parentid,
        role: '',
        classificationlevel: SecurityClassification.INTERNAL,
        name: entity.name,
      };
    }
    case HierarchyLevelType.Object: {
      return {
        ...(entity.id && { objectid: entity.id }),
        name: entity.name,
        sourceid: entity.sourceid,
        datalinkid: entity.datalinkid,
        objecttypeid: entity.objecttypeid,
      };
    }
    default:
      throw new Error('Not implemented');
  }
};

export const getOneEntityAction = (entity: Partial<IHierarchyTreeItem> & { objectid?: string }) => {
  if (entity.entityType === HierarchyLevelType.Object || entity.objectid) return;

  const actions = entity.entityType ? ENTITY_ACTIONS_MAPPER[entity.entityType] : null;
  if (!actions?.getOne) throw new Error('Not implemented');

  return actions.getOne(entity.id, entity.parentId);
};

export const getManyEntitiesAction = (entityType: HierarchyLevelType, params: Partial<IHierarchyAPIQuery>) => {
  const actions = ENTITY_ACTIONS_MAPPER[entityType];
  if (!actions?.getMany) throw new Error('Not implemented');

  return actions.getMany(params);
};

export const getCreateEntityAction = (entity: Partial<IHierarchyTreeItem>) => {
  const actions = entity.entityType ? ENTITY_ACTIONS_MAPPER[entity.entityType] : null;
  if (!actions?.create) throw new Error('Not implemented');

  return actions.create(mapTreeItemToEntity(entity));
};

export const getDefaultNewEntity = (entityType: HierarchyLevelType, parentId: string | null = null): Omit<IHierarchyTreeItem, 'id'> => {
  const newEntity = {
    entityType,
    parentId,
    name: hierarchyTypeNewLabel[entityType],
  };
  return newEntity;
};

export const getUpdateEntityAction = (entity: Partial<IHierarchyTreeItem>) => {
  const actions = entity.entityType ? ENTITY_ACTIONS_MAPPER[entity.entityType] : null;
  if (!actions?.update) throw new Error('Not implemented');

  return actions.update(mapTreeItemToEntity(entity));
};

export const deleteEntityAction = (entity: Partial<IHierarchyTreeItem>) => {
  const actions = entity.entityType ? ENTITY_ACTIONS_MAPPER[entity.entityType] : null;
  if (!actions?.delete) {
    throw new Error('Not implemented');
  }

  return actions.delete(entity);
};
